// Chess — top-level page.
//
// Phases:
//   lobby   — pick a stake
//   queue   — waiting for opponent (or bot backfill)
//   vs      — 2.8 s VS intro overlay
//   playing — live match (board + clocks + action row)
//   result  — match finished, result modal
//
// Structure follows Ludo / Rummy: top bar always on, shared
// SettingsMenu, voice wired via WSVoice on match start. Every class
// is prefixed `chess-*` so styling cannot leak into other games.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { setWalletBalance, useWallet } from '@baaligames/shared-ui/lib/wallet';
import Icon from '@baaligames/shared-ui/components/Icon';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import LobbyBanner from '@baaligames/shared-ui/components/LobbyBanner';
import ConcurrentMatchModal from '@baaligames/shared-ui/components/ConcurrentMatchModal';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { isSessionAcked, markSessionAcked, clearSessionAcked } from '@baaligames/shared-ui/lib/recoveryAck';
import LobbyScreen from './LobbyScreen';
import Board, { legalTargetsFrom, isPromotionMove } from './Board';
import PromotionModal from './PromotionModal';
import SeatCard from './SeatCard';
import CapturedPanel from './CapturedPanel';
import VsIntro from './VsIntro';
import RecoveryScreen, { type ChessRecoveryKind } from './RecoveryScreen';
import { ChessWS, type WSStatus } from './ws';
import { WSVoice } from './wsVoice';
import {
  primeAudio, setSfxEnabled,
  playTap, playSlotLock, playYourTurnChime,
  playMove, playCapture, playCastle,
  playCheck, playCheckmate, playDefeat, playDraw, playPromote,
  updateClockWarning, stopClockWarning,
} from './sounds';
import './chess.css';

type Phase = 'lobby' | 'queue' | 'vs' | 'playing' | 'result';

type ChessConfig = {
  audioEnabled: boolean;
  platformFeePercent: number;
  betTiers: number[];
  playerModes: number[];
  variant: string;
  baseMinutesPerSide: number;
  incrementSecondsPerMove: number;
  firstMoveGraceSeconds: number;
  humanStrikeLimit: number;
  // Server-driven UX knobs (added via Phase 3/4 wiring). Optional so
  // an older /api/chess/config response without these fields still
  // satisfies the type.
  disconnectGraceSeconds?: number;
  reconnectWindowSeconds?: number;
  abandonAction?: 'refund' | 'forfeit';
  urgencyBeepLeadSeconds?: number;
  lobbyBanner?: { enabled: boolean; text: string; tone: 'info' | 'warning' | 'critical' };
};

type SideProfile = {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  isBot: boolean;
  /** Bot difficulty (`bots.personality_type` from DB, or the bracket
   *  fallback for synthetic bots). Surfaces the EASY/MEDIUM/HARD
   *  tag in SeatCard so the player can see what they're up against.
   *  null for human players. */
  botMode?: string | null;
  /** Snapshot of the side's wallet at match start. Bots get their
   *  `walletLabel` cosmetic value; real humans get the live DB
   *  balance at the moment startSession debited them. */
  walletLabel?: number;
};
type MatchProfiles = { w: SideProfile; b: SideProfile };

type QueueState = {
  current: number;
  required: number;
  profiles: { userId?: string; name?: string; avatarUrl?: string | null; is_bot?: boolean }[];
  yourUserId: string | null;
  botActivateAt: number | null;
};

type Terminal =
  | { reason: 'checkmate'; winner: 'w' | 'b' }
  | { reason: 'stalemate' }
  | { reason: 'insufficient_material' }
  | { reason: 'fifty_move' }
  | { reason: 'threefold' }
  | { reason: 'agreed_draw' }
  | { reason: 'resign'; winner: 'w' | 'b' }
  | { reason: 'timeout'; winner?: 'w' | 'b' | null };

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function STARTING_FEN_CONST() { return 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'; }

// Error codes the WS / engine layers handle internally (re-auth,
// pending-offer races) — no need to alarm the user with a toast.
// Anything outside this set falls through the catch-all toast path.
const TRANSIENT_ERROR_CODES = new Set([
  'auth_failed',                  // WS class transparently re-authenticates
  'not_authed',                   // server restart: client queued a frame
                                  // before re-auth completed; the WS
                                  // class re-auths + replays in <1 s,
                                  // but a multi-second cascade of these
                                  // toasts kept popping right before the
                                  // bot appeared to "freeze". Suppress.
  'offer_already_pending',        // user double-clicked draw-offer
  'no_offer_from_opponent',       // accept/decline arrived after offer cleared
  'unknown_type',                 // safe-to-ignore protocol noise
]);

// Stable list of every URL the Chess lobby + match screen depends on.
// Module-scoped so the array identity is fixed; useAssetPreloader
// would otherwise restart on every render. Pieces glyphs are inline
// SVG (no URL), so this only needs the lobby art.
const CHESS_PRELOAD_URLS: string[] = [
  '/games/chess/Logo.png',
  '/games/chess/Coin.png',
  '/games/chess/Plus.png',
  '/games/chess/Settings.png',
  '/games/chess/Cash-1.png',
  '/games/chess/Cash-2.png',
  '/games/chess/Cash-3.png',
  '/games/chess/Cash-4.png',
  '/games/chess/Victory_Trophy.png',
  '/games/chess/Defeat_Medal.png',
  '/games/chess/Quit_Warning.png',
];

export default function Chess() {
  const { user } = useAuth();
  // Bootstrap gate — see the matching block in Ludo / XO. The loading
  // overlay covers the route until both `authReady` AND asset preload
  // have completed.
  const assetStatus = useAssetPreloader(CHESS_PRELOAD_URLS, 600);
  const [authReady, setAuthReady] = useState(false);
  const { balance } = useWallet();
  const toast = useToast();
  const nav = useNavigate();

  // ─── Top-level config + phase ────────────────────────────────────
  const [config, setConfig] = useState<ChessConfig | null>(null);
  const [phase,  setPhase]  = useState<Phase>('lobby');
  const phaseRef = useRef<Phase>('lobby');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const [selectedBet, setSelectedBet] = useState<number | null>(null);
  const [online,      setOnline]      = useState(0);
  const [waitingByBet, setWaitingByBet] = useState<Record<string, number>>({});
  const [showWaitingBadge, setShowWaitingBadge] = useState(true);

  const [queueState, setQueueState] = useState<QueueState | null>(null);

  // Concurrent-match modal state — opens when join_queue rejects with
  // `concurrent_match_limit`. Holds the blocking-match list returned
  // by the server so the modal can render the precise "you're in
  // Chess (₹100), Rummy (₹100)" message + Quit & lose button.
  const [concBlocking, setConcBlocking] = useState<any[] | null>(null);
  const [concOpen,     setConcOpen]     = useState(false);

  // ─── Settings / voice ────────────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [micMuted, setMicMuted] = useState(false);
  const [listenMuted, setListenMuted] = useState(false);
  useEffect(() => { setSfxEnabled(soundOn); }, [soundOn]);

  // ─── Match state (valid only in vs / playing / result) ───────────
  const [mySide,   setMySide]   = useState<'w' | 'b' | null>(null);
  const [profiles, setProfiles] = useState<MatchProfiles | null>(null);
  const [pot,      setPot]      = useState(0);
  const [fen,      setFen]      = useState<string>(STARTING_FEN_CONST());
  const [turn,     setTurn]     = useState<'w' | 'b'>('w');
  const [clocks,   setClocks]   = useState<{ w: number; b: number }>({ w: 300_000, b: 300_000 });
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
  const [checkSq,  setCheckSq]  = useState<string | null>(null);
  const [drawOffer, setDrawOffer] = useState<'w' | 'b' | null>(null);
  const [disconnected, setDisconnected] = useState<{ w: boolean; b: boolean }>({ w: false, b: false });
  const [terminal, setTerminal] = useState<Terminal | null>(null);

  // ─── UI selection state (click-to-move) ─────────────────────────
  const [selectedSq,   setSelectedSq]   = useState<string | null>(null);
  const [legalTargets, setLegalTargets] = useState<string[]>([]);
  const [promoPending, setPromoPending] = useState<{ from: string; to: string } | null>(null);

  // ─── Recovery popup (previous-game) ─────────────────────────────
  type Recovery = {
    sessionId: number;
    kind: ChessRecoveryKind;
    amount: number;
    pot: number;
    platformFee?: number;
    opponents?: Array<{ name?: string; isBot?: boolean }>;
  };
  const [recovery, setRecovery] = useState<Recovery | null>(null);

  // ─── Refs + helpers ─────────────────────────────────────────────
  const wsRef    = useRef<ChessWS | null>(null);
  const voiceRef = useRef<WSVoice | null>(null);
  const mySideRef = useRef<'w' | 'b' | null>(null);
  useEffect(() => { mySideRef.current = mySide; }, [mySide]);

  // Visible connection-status indicator so a WS drop mid-match
  // doesn't leave the user staring at a frozen board with no clue
  // why. Matches Ludo's `.status-box` affordance.
  const [wsStatus, setWsStatus] = useState<WSStatus>('connecting');

  // Clock-warning scheduler: watches the on-move side's clock and
  // triggers the tick-tick cadence from sounds.ts. Runs on a 200 ms
  // interval because the clock display itself only ticks every
  // 100 ms — 200 ms is plenty to pick up threshold crossings
  // without burning CPU. Cleans up on phase change + unmount.
  useEffect(() => {
    if (phase !== 'playing') {
      stopClockWarning();
      return;
    }
    const isActiveSideClock = () => {
      const lastSync = Date.now();
      const value = clocks[turn] || 0;
      return Math.max(0, value - (Date.now() - lastSync));
    };
    // Lead time is admin-controlled via /api/chess/config →
    // urgencyBeepLeadSeconds (default 60). Falls back if the config
    // hasn't resolved yet so the warning still fires.
    const leadSec = Number(config?.urgencyBeepLeadSeconds ?? 60);
    // Initialise once synchronously so a clock that STARTS under
    // the lead window (e.g., on reconnect) triggers the warning immediately.
    updateClockWarning(isActiveSideClock(), phase === 'playing', leadSec);
    const id = window.setInterval(() => {
      updateClockWarning(isActiveSideClock(), phase === 'playing', leadSec);
    }, 200);
    return () => { window.clearInterval(id); stopClockWarning(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, turn, clocks, config?.urgencyBeepLeadSeconds]);

  // Orientation = always render from our own side's POV. During the
  // initial VS window mySide might be null (falls back to white view).
  const orientation: 'white' | 'black' = mySide === 'b' ? 'black' : 'white';

  // ─── Fetch config + recovery on mount ───────────────────────────
  useEffect(() => {
    fetch('/api/chess/config')
      .then((r) => r.json())
      .then((j) => {
        if (!j?.ok) return;
        setConfig(j.data);
        if (selectedBet == null && Array.isArray(j.data?.betTiers) && j.data.betTiers.length) {
          const nonZero = j.data.betTiers.filter((x: number) => x > 0);
          setSelectedBet(nonZero[0] ?? null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch('/api/chess/pending-result', { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const p = j?.data?.pending;
        if (!p || !p.sessionId) return;
        if (isSessionAcked('chess', p.sessionId)) {
          fetch('/api/chess/acknowledge-result', {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId: p.sessionId }),
          })
            .then(() => clearSessionAcked('chess', p.sessionId))
            .catch(() => {});
          return;
        }
        const kind = (String(p.result || 'loss') as ChessRecoveryKind);
        const amount = kind === 'win' ? Number(p.payout || 0)
                     : kind === 'draw' ? Number(p.bet_amount || p.betAmount || 0)
                     : Number(p.bet_amount || p.betAmount || 0);
        // Wait for the WS to settle first so we never overlap an
        // active match reconnect_sync.
        setTimeout(() => {
          if (cancelled) return;
          if (phaseRef.current !== 'lobby') return;
          setRecovery({
            sessionId: Number(p.sessionId),
            kind, amount,
            pot: Number(p.pot || 0),
            platformFee: Number(p.platformFee || p.platform_fee || 0),
            opponents: Array.isArray(p.opponents) ? p.opponents : [],
          });
        }, 1200);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  function acknowledgeRecovery() {
    if (!recovery) return;
    const sid = recovery.sessionId;
    markSessionAcked('chess', sid);
    fetch('/api/chess/acknowledge-result', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sid }),
    })
      .then(() => clearSessionAcked('chess', sid))
      .catch(() => {})
      .finally(() => setRecovery(null));
  }

  // ─── Voice mount helper ────────────────────────────────────────
  const startVoice = useCallback((mine: 'w' | 'b' | null) => {
    // NOTE: the earlier version gated on `config?.audioEnabled`
    // which looked sensible until you realised `config` is fetched
    // via `GET /api/chess/config` and the `chess_match_start`
    // message routinely arrives before that GET resolves — leaving
    // `config` null and `startVoice` silently returning. Ludo hit
    // and fixed the exact same bug; this matches Ludo's approach.
    // The server-side `ChessGame.AUDIO_ENABLED` kill-switch still
    // enforces the "voice off globally" policy in `relayVoiceChunk`,
    // so dropping this gate is safe.
    if (!mine) return;
    voiceRef.current?.stop();
    voiceRef.current = null;
    const v = new WSVoice(mine, (m) => { wsRef.current?.send(m); });
    if (micMuted)    v.setMuted(true);
    if (listenMuted) v.setListenMuted(true);
    voiceRef.current = v;
    v.start().then((ok) => {
      if (ok) {
        console.info('[chess/voice] started as side', mine);
        toast.push('success', 'Voice chat enabled', 1800);
      } else {
        toast.push('error', 'Mic blocked — check browser permissions');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micMuted, listenMuted]);

  const stopVoice = useCallback(() => {
    try { voiceRef.current?.stop(); } catch {}
    voiceRef.current = null;
  }, []);

  // ─── WS mount + message routing ────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const ws = new ChessWS();
    wsRef.current = ws;
    const off       = ws.on((msg: any) => handleMessage(msg));
    const offStatus = ws.onStatus(setWsStatus);
    ws.connect();
    return () => {
      off();
      offStatus();
      ws.close();
      stopVoice();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function handleMessage(msg: any) {
    if (!msg || typeof msg.type !== 'string') return;
    switch (msg.type) {
      case 'chess_auth_success':
        // Server confirms auth — flip the bootstrap gate.
        setAuthReady(true);
        break;

      case 'chess_global_stats':
        if (typeof msg.online_players === 'number') setOnline(msg.online_players);
        if (typeof msg.show_waiting_badge === 'boolean') setShowWaitingBadge(msg.show_waiting_badge);
        if (msg.waiting_by_bet && typeof msg.waiting_by_bet === 'object') {
          setWaitingByBet(msg.waiting_by_bet);
        }
        break;

      case 'chess_waiting_in_queue':
        setPhase('queue');
        setQueueState({
          current:  Number(msg.current_players  || 0),
          required: Number(msg.required_players || 2),
          profiles: Array.isArray(msg.profiles) ? msg.profiles : [],
          yourUserId: msg.your_user_id || null,
          botActivateAt: typeof msg.bot_activate_at === 'number' ? msg.bot_activate_at : null,
        });
        break;

      case 'chess_you_are_side':
        setMySide(msg.side);
        mySideRef.current = msg.side;
        break;

      case 'chess_match_start': {
        const rawProfiles = msg.profiles || {};
        const w: SideProfile = {
          userId:      String(rawProfiles.w?.userId ?? 'w'),
          displayName: String(rawProfiles.w?.displayName ?? 'White'),
          avatarUrl:   rawProfiles.w?.avatarUrl || null,
          isBot:       !!rawProfiles.w?.isBot,
          botMode:     rawProfiles.w?.isBot ? (rawProfiles.w?.botMode || null) : null,
          walletLabel: Number(rawProfiles.w?.walletLabel ?? rawProfiles.w?.wallet ?? 0) || 0,
        };
        const b: SideProfile = {
          userId:      String(rawProfiles.b?.userId ?? 'b'),
          displayName: String(rawProfiles.b?.displayName ?? 'Black'),
          avatarUrl:   rawProfiles.b?.avatarUrl || null,
          isBot:       !!rawProfiles.b?.isBot,
          botMode:     rawProfiles.b?.isBot ? (rawProfiles.b?.botMode || null) : null,
          walletLabel: Number(rawProfiles.b?.walletLabel ?? rawProfiles.b?.wallet ?? 0) || 0,
        };
        setProfiles({ w, b });
        setPot(Number(msg.pot || 0));
        setFen(String(msg.position || STARTING_FEN_CONST()));
        setTurn('w');
        setClocks({ w: Number(msg.clocks?.w || 0), b: Number(msg.clocks?.b || 0) });
        setLastMove(null);
        setCheckSq(null);
        setDrawOffer(null);
        setTerminal(null);
        setSelectedSq(null);
        setLegalTargets([]);
        setPromoPending(null);
        setDisconnected({ w: false, b: false });
        // VS intro for ~2.8 s, then the live board becomes interactive.
        setPhase('vs');
        primeAudio();
        if (mySideRef.current) startVoice(mySideRef.current);
        setTimeout(() => {
          if (phaseRef.current === 'vs') setPhase('playing');
        }, 2800);
        break;
      }

      case 'chess_game_resumed': {
        // Reconnect landed in a live match — skip VS + restore state.
        const rawProfiles = msg.profiles || {};
        setProfiles({
          w: {
            userId: String(rawProfiles.w?.userId ?? 'w'),
            displayName: String(rawProfiles.w?.displayName ?? 'White'),
            avatarUrl: rawProfiles.w?.avatarUrl || null,
            isBot: !!rawProfiles.w?.isBot,
            botMode: rawProfiles.w?.isBot ? (rawProfiles.w?.botMode || null) : null,
            walletLabel: Number(rawProfiles.w?.walletLabel ?? rawProfiles.w?.wallet ?? 0) || 0,
          },
          b: {
            userId: String(rawProfiles.b?.userId ?? 'b'),
            displayName: String(rawProfiles.b?.displayName ?? 'Black'),
            avatarUrl: rawProfiles.b?.avatarUrl || null,
            isBot: !!rawProfiles.b?.isBot,
            botMode: rawProfiles.b?.isBot ? (rawProfiles.b?.botMode || null) : null,
            walletLabel: Number(rawProfiles.b?.walletLabel ?? rawProfiles.b?.wallet ?? 0) || 0,
          },
        });
        setPot(Number(msg.pot || 0));
        setFen(String(msg.position || STARTING_FEN_CONST()));
        setTurn(msg.turn || 'w');
        setClocks({ w: Number(msg.clocks?.w || 0), b: Number(msg.clocks?.b || 0) });
        setMySide(msg.your_side);
        mySideRef.current = msg.your_side;
        setDrawOffer(msg.drawOffer || null);
        setPhase('playing');
        primeAudio();
        if (msg.your_side) startVoice(msg.your_side);
        break;
      }

      case 'chess_move_made': {
        // Apply to UI state — FEN is the source of truth for board
        // contents, captured pieces, material.
        setFen(String(msg.resultingFen));
        const newTurn = msg.resultingFen.split(' ')[1] as 'w' | 'b';
        setTurn(newTurn);
        setClocks({ w: Number(msg.clocks?.w || 0), b: Number(msg.clocks?.b || 0) });
        setLastMove({ from: msg.from, to: msg.to });
        setCheckSq(msg.check ? findKingSquare(String(msg.resultingFen), newTurn) : null);
        // If the new active side is ME and the previous move wasn't
        // mine, play the your-turn chime (distinct timbre).
        if (msg.by !== mySideRef.current && newTurn === mySideRef.current) {
          playYourTurnChime();
        }
        // Move / capture / castle / promote SFX.
        if (msg.castle) playCastle();
        else if (msg.promoteTo) playPromote();
        else if (msg.capture)   playCapture();
        else                    playMove();
        if (msg.check) playCheck();

        // Clear selection on any move so stale highlights don't linger.
        setSelectedSq(null); setLegalTargets([]);
        setPromoPending(null);

        // A pending draw offer FROM us is silently withdrawn on our move.
        // A pending offer FROM opponent is implicitly declined — the
        // chess_draw_declined event fires separately.
        if (drawOffer === msg.by) setDrawOffer(null);

        if (msg.terminal) {
          setTerminal(msg.terminal);
          // chess_game_over will arrive next to flip phase=result.
        }
        break;
      }

      case 'chess_draw_offered':
        setDrawOffer(msg.by);
        if (msg.by !== mySideRef.current) toast.push('info', 'Opponent offered a draw');
        break;
      case 'chess_draw_declined':
        setDrawOffer(null);
        if (msg.by !== mySideRef.current) toast.push('info', 'Draw declined');
        break;

      case 'chess_player_disconnected':
        setDisconnected((d) => ({ ...d, [msg.side]: true }));
        if (msg.side !== mySideRef.current) toast.push('info', 'Opponent disconnected — reconnect timer started');
        break;
      case 'chess_player_reconnected':
        setDisconnected((d) => ({ ...d, [msg.side]: false }));
        break;

      case 'chess_game_over': {
        setTerminal({
          reason: msg.termination,
          winner: msg.winner || null,
        } as Terminal);
        setFen(String(msg.finalFen || STARTING_FEN_CONST()));
        setClocks({ w: Number(msg.finalClocks?.w || 0), b: Number(msg.finalClocks?.b || 0) });
        // Terminal sound effects.
        const me = mySideRef.current;
        if (msg.winner && me && msg.winner === me) {
          playCheckmate();
        } else if (!msg.winner) {
          playDraw();
        } else {
          playDefeat();
        }
        setPhase('result');
        stopVoice();
        break;
      }

      case 'chess_session_aborted':
        toast.push('info', msg.note || 'Match cancelled');
        resetToLobby();
        break;

      case 'chess_left_game_success':
        resetToLobby();
        break;

      case 'wallet_update':
        if (typeof msg.balance === 'number') setWalletBalance(msg.balance);
        break;

      case 'voice_chunk':
        if (msg.from && typeof msg.pcm === 'string') {
          voiceRef.current?.onRemoteChunk(msg.from, msg.pcm);
        }
        break;

      case 'error':
        // Common errors → toast with human-readable text; silent-ignore
        // purely-informational codes (e.g. opponent-move declines an
        // already-nullified offer).
        if (msg.code === 'insufficient_funds') {
          toast.push('error', 'Not enough balance');
          resetToLobby();
        } else if (msg.code === 'concurrent_match_limit') {
          // Open the friendly modal instead of a hostile error toast.
          // The modal shows blocking match details + a Quit button.
          setConcBlocking(Array.isArray(msg.blocking) ? msg.blocking : []);
          setConcOpen(true);
          resetToLobby();
        } else if (msg.code === 'daily_loss_cap') {
          toast.push('error', 'You\'ve reached your daily loss limit. Try again tomorrow.');
          resetToLobby();
        } else if (msg.code === 'bet_limit') {
          toast.push('error', msg.message || 'This bet is above your limit — contact your agent.');
          resetToLobby();
        } else if (msg.code === 'illegal_move') {
          // Should never fire under normal play — our client filters
          // via legalMovesFrom — but show a toast so the user isn't
          // left wondering why nothing happened.
          toast.push('error', 'Illegal move');
        } else if (msg.code && !TRANSIENT_ERROR_CODES.has(msg.code)) {
          toast.push('error', msg.reason || msg.code);
        }
        break;
    }
  }

  function resetToLobby() {
    setPhase('lobby');
    setQueueState(null);
    setProfiles(null);
    setMySide(null);
    setTerminal(null);
    setSelectedSq(null);
    setLegalTargets([]);
    setPromoPending(null);
    stopVoice();
  }

  // ─── Lobby actions ──────────────────────────────────────────────
  function handleSelectBet(n: number) {
    setSelectedBet(n);
    if ((balance ?? 0) < n) {
      toast.push('error', 'Not enough balance');
      return;
    }
    primeAudio();
    playTap();

    // Pre-warm the mic INSIDE this user gesture. Do NOT gate on
    // `config?.audioEnabled` — the config GET may not have resolved
    // yet, and missing the gesture window means iOS Safari / mobile
    // Chrome will silently deny the mic when `WSVoice.start()` asks
    // later. Server-side `AUDIO_ENABLED` still drops frames if voice
    // is disabled globally, so pre-warming "extra" is harmless.
    if (navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => { (window as any).__chessMicStream = stream; })
        .catch((e) => { console.warn('[chess/voice] mic pre-warm declined:', e); });
    }

    // `send()` returns false when the WS isn't OPEN yet. Tell the
    // user instead of silently advancing the phase to a view that
    // depends on a server round-trip that will never arrive.
    const sent = wsRef.current?.send({ type: 'join_queue', bet: n });
    if (!sent) {
      toast.push('error', 'Still connecting — try again in a moment');
      return;
    }
    setPhase('queue');
  }

  function handleCancelQueue() {
    // Fire-and-forget — the Cancel button in the queue UI ALSO calls
    // `resetToLobby()` immediately so the user is never stuck on
    // queue view even if the WS can't deliver the message.
    wsRef.current?.send({ type: 'leave_game' });
  }

  // ─── Match actions ──────────────────────────────────────────────
  function handleSquareClick(sq: string) {
    if (phase !== 'playing') return;
    if (!mySide || turn !== mySide) return;

    // 1. Clicking a dot-highlighted target from current selection → move.
    if (selectedSq && legalTargets.includes(sq)) {
      if (isPromotionMove(fen, selectedSq, sq)) {
        setPromoPending({ from: selectedSq, to: sq });
        return;
      }
      wsRef.current?.send({ type: 'move', from: selectedSq, to: sq });
      setSelectedSq(null); setLegalTargets([]);
      return;
    }

    // 2. Clicking one of our own pieces → (re)select.
    const pieceAtSq = pieceAt(fen, sq);
    if (pieceAtSq && pieceAtSq[0] === mySide) {
      setSelectedSq(sq);
      setLegalTargets(legalTargetsFrom(fen, sq));
      playTap();
      return;
    }

    // 3. Anything else → clear selection. (We deliberately never
    //    highlight opponent legal moves.)
    setSelectedSq(null); setLegalTargets([]);
  }

  function handlePromote(piece: 'q' | 'r' | 'b' | 'n') {
    if (!promoPending) return;
    wsRef.current?.send({
      type: 'move',
      from: promoPending.from,
      to:   promoPending.to,
      promoteTo: piece,
    });
    setPromoPending(null);
    setSelectedSq(null);
    setLegalTargets([]);
  }

  const [confirmResign, setConfirmResign] = useState(false);
  function handleResignClick()  { playTap(); setConfirmResign(true); }
  function handleResignCancel() { setConfirmResign(false); }
  function handleResignConfirm() {
    setConfirmResign(false);
    wsRef.current?.send({ type: 'resign' });
  }

  function handleOfferDraw() {
    playTap();
    wsRef.current?.send({ type: 'draw_offer' });
  }
  function handleAcceptDraw() {
    playTap();
    wsRef.current?.send({ type: 'draw_accept' });
  }
  function handleDeclineDraw() {
    playTap();
    wsRef.current?.send({ type: 'draw_decline' });
  }

  // Matches server's terminal detection: claim buttons only enabled
  // when the position is actually claimable.
  const halfmoveClockFromFen = useMemo(() => {
    const parts = fen.trim().split(/\s+/);
    return Number(parts[4] || 0);
  }, [fen]);

  function handleClaim50Move() {
    playTap();
    wsRef.current?.send({ type: 'claim_draw', reason: '50_move' });
  }

  // ─── Terminal/result helpers ────────────────────────────────────
  const resultKind: ChessRecoveryKind | null = useMemo(() => {
    if (!terminal) return null;
    if ('winner' in terminal && terminal.winner) {
      return terminal.winner === mySide ? 'win' : 'loss';
    }
    return 'draw';
  }, [terminal, mySide]);

  const resultAmount = useMemo(() => {
    if (!terminal || !selectedBet) return 0;
    const bet = selectedBet;
    if (resultKind === 'win') {
      const feePct = config?.platformFeePercent ?? 0;
      return Math.floor(pot * (100 - feePct) / 100);
    }
    if (resultKind === 'draw') return bet;  // refund
    return bet;                              // forfeit = bet
  }, [terminal, selectedBet, resultKind, pot, config?.platformFeePercent]);

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="chess-root">
      {/* Loading overlay — covers the route until WS auth AND every
          preloaded asset URL have settled. */}
      <GameLoadingScreen
        visible={!authReady || !assetStatus.ready}
        theme="chess"
        progress={assetStatus.progress}
      />
      <ConcurrentMatchModal
        open={concOpen}
        blocking={concBlocking}
        walletBalance={balance}
        onResolved={() => {
          setConcOpen(false);
          setConcBlocking(null);
          toast.push('success', 'Active matches resolved — try again', 2000);
        }}
        onCancel={() => setConcOpen(false)}
      />
      <div className="chess-app-container">
        <GameSettingsMenu
          open={settingsOpen}
          soundOn={soundOn}
          micMuted={micMuted}
          listenMuted={listenMuted}
          voiceEnabled={!!config?.audioEnabled && (phase === 'playing' || phase === 'vs')}
          onToggleSound={() => { playTap(); setSoundOn((v) => !v); }}
          onToggleMic={() => {
            playTap();
            setMicMuted((v) => {
              const n = !v;
              voiceRef.current?.setMuted(n);
              return n;
            });
          }}
          onToggleListen={() => {
            playTap();
            setListenMuted((v) => {
              const n = !v;
              voiceRef.current?.setListenMuted(n);
              if (!n) voiceRef.current?.kickAudioPlayback?.();
              return n;
            });
          }}
          onExit={() => {
            playTap();
            setSettingsOpen(false);
            if (phase === 'playing' || phase === 'vs') setConfirmResign(true);
            else if (phase === 'queue') handleCancelQueue();
            else nav('/');
          }}
          onClose={() => setSettingsOpen(false)}
          exitLabel={
            (phase === 'playing' || phase === 'vs')
              ? 'Resign Match'
              : phase === 'queue' ? 'Leave Queue' : 'Exit to Home'
          }
          anchor="left"
        />

        {/* Connection-status chip — only visible when the WS is not
            fully open. Hides once `authReady`-equivalent (an auth
            success arrives = `chess_auth_success` → status 'open'). */}
        {wsStatus !== 'open' && (
          <div className="chess-status-chip">
            {wsStatus === 'lost'
              ? 'Connection lost · reconnecting…'
              : wsStatus === 'reconnecting'
                ? 'Reconnecting to server…'
                : 'Connecting to server…'}
          </div>
        )}

        <ChessTopBar
          walletAmt={balance ?? 0}
          potDuringMatch={(phase === 'playing' || phase === 'vs')
            ? { pot, payout: Math.floor(pot * (100 - (config?.platformFeePercent ?? 0)) / 100) }
            : null}
          onBack={() => {
            playTap();
            if (phase === 'playing' || phase === 'vs') {
              setConfirmResign(true);
            } else if (phase === 'queue') {
              // Always drop to lobby locally so a flaky WS doesn't
              // trap the user on the queue screen. The server-side
              // `leave_game` is best-effort.
              handleCancelQueue();
              resetToLobby();
            } else {
              nav('/');
            }
          }}
          onSettings={() => { playTap(); setSettingsOpen((o) => !o); }}
          onAddFunds={() => nav('/wallet')}
        />

        {/* ── LOBBY ────────────────────────────────────────────── */}
        {phase === 'lobby' && config && (
          <LobbyBanner
            enabled={config.lobbyBanner?.enabled}
            text={config.lobbyBanner?.text}
            tone={config.lobbyBanner?.tone}
          />
        )}
        {phase === 'lobby' && config && (
          <LobbyScreen
            authReady={!!user}
            onlinePlayers={online}
            betTiers={config.betTiers}
            selectedBet={selectedBet}
            platformFeePercent={config.platformFeePercent}
            waitingByBet={waitingByBet}
            showWaitingBadge={showWaitingBadge}
            baseMinutes={config.baseMinutesPerSide}
            incrementSeconds={config.incrementSecondsPerMove}
            onSelectBet={handleSelectBet}
          />
        )}

        {/* Recovery popup — only in lobby phase. */}
        {phase === 'lobby' && recovery && (
          <RecoveryScreen
            kind={recovery.kind}
            amount={recovery.amount}
            pot={recovery.pot}
            platformFee={recovery.platformFee}
            opponents={recovery.opponents}
            onAck={acknowledgeRecovery}
          />
        )}

        {/* ── QUEUE ──────────────────────────────────────────────
            Mirrors Ludo / Rummy's waiting-modal pattern: renders
            one avatar slot per required player, filled slots show
            the user's avatar + name, empty slots show a hourglass
            placeholder with a subtle pulse. Empty slots fill in
            real-time as `chess_waiting_in_queue` events arrive. */}
        {phase === 'queue' && (
          <div className="chess-queue">
            <div className="chess-queue-card">
              <div className="chess-queue-title">
                {queueState ? 'Waiting for opponent' : 'Joining queue…'}
              </div>
              <div className="chess-queue-slots">
                {[0, 1].map((i) => {
                  const slot = queueState?.profiles?.[i];
                  const yours = slot && queueState?.yourUserId && slot.userId === queueState.yourUserId;
                  return (
                    <div
                      key={i}
                      className={[
                        'chess-queue-slot',
                        slot ? 'chess-queue-slot--filled' : 'chess-queue-slot--empty',
                        yours ? 'chess-queue-slot--you' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="chess-queue-slot__avatar">
                        {slot
                          ? (slot.avatarUrl
                              ? <img src={slot.avatarUrl} alt="" />
                              : <span>{(slot.name || '?').slice(0, 1).toUpperCase()}</span>)
                          : <Icon name="hourglass_top" size={22} />
                        }
                      </div>
                      <div className="chess-queue-slot__name">
                        {slot
                          ? (yours ? 'You' : slot.name || 'Player')
                          : 'Waiting…'}
                      </div>
                      {/* "BOT" tag removed — players must never see
                          that an opponent slot was filled by a bot. */}
                    </div>
                  );
                })}
              </div>
              <div className="chess-queue-sub">
                {queueState
                  ? `${queueState.current} / ${queueState.required} joined`
                  : 'Connecting to the server'}
              </div>
              {queueState?.botActivateAt && (
                <BotActivateCountdown ts={queueState.botActivateAt} />
              )}
              <button
                type="button"
                className="chess-queue-cancel"
                onClick={() => {
                  playTap();
                  handleCancelQueue();
                  resetToLobby();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── VS + PLAYING + RESULT share the board layout ───────
            Layout per user spec:
              • Opponent seat at top, their captured-pieces tray
                directly BELOW the profile (showing pieces THEY
                captured, i.e., MY lost pieces).
              • Board centered vertically.
              • My captured-pieces tray ABOVE my profile (showing
                pieces I captured = opponent's lost pieces).
              • My seat at the bottom. */}
        {(phase === 'vs' || phase === 'playing' || phase === 'result') && profiles && (
          <div className="chess-felt">
            {(() => {
              const oppSide = (mySide === 'w' ? 'b' : 'w') as 'w' | 'b';
              const meSide  = (mySide || 'w') as 'w' | 'b';
              return (
                <>
                  {/* Opponent: profile + their captures tray. */}
                  <div className="chess-seat-area">
                    <SeatCard
                      name={profiles[oppSide].displayName}
                      avatarUrl={profiles[oppSide].avatarUrl}
                      isBot={profiles[oppSide].isBot}
                      botMode={profiles[oppSide].botMode}
                      walletBalance={profiles[oppSide].walletLabel}
                      clockMs={clocks[oppSide]}
                      isActive={turn === oppSide}
                      isMe={false}
                      side="top"
                      disconnected={disconnected[oppSide]}
                    />
                    <div className="chess-seat-area__tray">
                      <CapturedPanel fen={fen} side={oppSide} />
                    </div>
                  </div>

                  <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Board
                      fen={fen}
                      orientation={orientation}
                      turn={turn}
                      mySide={mySide}
                      lastMove={lastMove}
                      checkSq={checkSq}
                      selectedSq={selectedSq}
                      legalTargets={legalTargets}
                      onSquareClick={handleSquareClick}
                      disabled={phase !== 'playing'}
                    />
                    {/* Full-screen CHECK banner — only shown when
                        it's OUR king being threatened, not the
                        opponent's. We derive that from the FEN's
                        active-colour field + checkSq being set. */}
                    {checkSq && phase === 'playing' && mySide && turn === mySide && (
                      <div className="chess-check-banner" aria-label="Check">
                        CHECK
                      </div>
                    )}
                  </div>

                  {/* Mine: captures tray above, profile below. */}
                  <div className="chess-seat-area">
                    <div className="chess-seat-area__tray">
                      <CapturedPanel fen={fen} side={meSide} />
                    </div>
                    <SeatCard
                      name={profiles[meSide].displayName}
                      avatarUrl={profiles[meSide].avatarUrl}
                      isBot={profiles[meSide].isBot}
                      botMode={profiles[meSide].botMode}
                      walletBalance={balance ?? profiles[meSide].walletLabel}
                      clockMs={clocks[meSide]}
                      isActive={turn === meSide}
                      isMe={true}
                      side="bottom"
                      disconnected={disconnected[meSide]}
                    />
                  </div>
                </>
              );
            })()}

            {/* Action row — visible only during the live match. */}
            {phase === 'playing' && (
              <div className="chess-actions">
                {/* Incoming draw offer from opponent → Accept / Decline */}
                {drawOffer && drawOffer !== mySide && (
                  <>
                    <button
                      className="chess-action-btn chess-action-btn--accept"
                      onClick={handleAcceptDraw}
                    >
                      <Icon name="handshake" size={14} /> Accept Draw
                    </button>
                    <button
                      className="chess-action-btn"
                      onClick={handleDeclineDraw}
                    >
                      <Icon name="close" size={14} /> Decline
                    </button>
                  </>
                )}
                {/* Our offer is pending → show withdraw hint (any move auto-withdraws too). */}
                {drawOffer && drawOffer === mySide && (
                  <span className="chess-actions-hint">Draw offer sent</span>
                )}
                {/* No active offer → normal Offer Draw button. */}
                {!drawOffer && (
                  <button
                    className="chess-action-btn"
                    onClick={handleOfferDraw}
                    aria-label="Offer a draw"
                  >
                    <Icon name="handshake" size={14} /> Offer Draw
                  </button>
                )}
                {/* 50-move claim only when eligible. */}
                {halfmoveClockFromFen >= 100 && (
                  <button
                    className="chess-action-btn"
                    onClick={handleClaim50Move}
                    aria-label="Claim 50-move draw"
                  >
                    <Icon name="timer" size={14} /> Claim 50-Move
                  </button>
                )}
                <button
                  className="chess-action-btn chess-action-btn--danger"
                  onClick={handleResignClick}
                  aria-label="Resign"
                >
                  <Icon name="flag" size={14} /> Resign
                </button>
              </div>
            )}

            {/* Promotion modal — non-dismissible. */}
            <PromotionModal
              open={Boolean(promoPending)}
              side={mySide || 'w'}
              onPick={handlePromote}
            />

            {/* VS intro overlay. Server's first-move grace covers it. */}
            {phase === 'vs' && (
              <VsIntro
                mySide={mySide || 'w'}
                profiles={{
                  w: { side: 'w', ...profiles.w },
                  b: { side: 'b', ...profiles.b },
                }}
                pot={pot}
                payout={Math.floor(pot * (100 - (config?.platformFeePercent ?? 0)) / 100)}
              />
            )}

            {/* Resign-confirm modal. */}
            {confirmResign && (
              <div className="chess-result-backdrop">
                <div className="chess-result-card">
                  <img
                    src="/games/chess/Quit_Warning.png"
                    alt=""
                    className="chess-result-icon"
                    draggable={false}
                  />
                  <h2 className="chess-result-title">Resign match?</h2>
                  <p className="chess-result-subtitle">
                    You'll forfeit your bet to your opponent.
                  </p>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', width: '100%' }}>
                    <button
                      type="button"
                      className="chess-result-btn"
                      onClick={handleResignCancel}
                      style={{ background: '#3a2515' }}
                    >
                      Stay
                    </button>
                    <button
                      type="button"
                      className="chess-result-btn chess-result-btn--danger"
                      onClick={handleResignConfirm}
                    >
                      Resign
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Post-match result overlay. */}
        {phase === 'result' && terminal && resultKind && (
          <RecoveryScreen
            kind={resultKind}
            amount={resultAmount}
            pot={pot}
            platformFee={Math.floor(pot * (config?.platformFeePercent ?? 0) / 100)}
            opponents={profiles
              ? [{ name: profiles[mySide === 'w' ? 'b' : 'w']?.displayName, isBot: profiles[mySide === 'w' ? 'b' : 'w']?.isBot }]
              : []}
            onAck={() => { playTap(); resetToLobby(); }}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ChessTopBar — identical shape to Ludo's TopBar (back pill + settings
// cog on the left, optional pot chip in the centre, wallet chip on
// the right). Wood-tone gradient instead of Ludo's purple.
// ─────────────────────────────────────────────────────────────────────
function ChessTopBar({
  walletAmt, potDuringMatch, onBack, onSettings, onAddFunds,
}: {
  walletAmt: number;
  potDuringMatch: { pot: number; payout: number } | null;
  onBack: () => void;
  onSettings: () => void;
  onAddFunds: () => void;
}) {
  return (
    <div className="chess-topbar">
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          title="Back"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
        >
          <Icon name="arrow_back" size={22} filled />
        </button>
        <button
          type="button"
          onClick={onSettings}
          aria-label="Settings"
          title="Settings"
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
        >
          <img src="/games/chess/Settings.png" alt="" className="w-full h-full object-contain" />
        </button>
      </div>
      {potDuringMatch ? (
        <div className="flex-1 flex items-center justify-center min-w-0">
          <div className="bg-gradient-to-b from-[#5a3f26] to-[#2b1a0e] rounded-lg flex items-center justify-center px-3 py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)]">
            <Icon name="savings" size={18} filled className="text-[#f6d07c] mr-2" />
            <div className="flex flex-col items-start leading-none mr-1 sm:mr-2">
              <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.2em] text-white/70">WIN</span>
              <span className="text-white font-bold text-base sm:text-lg tracking-wide tabular-nums mt-0.5">
                {fmt(potDuringMatch.payout)}
              </span>
            </div>
          </div>
        </div>
      ) : <div className="flex-1" />}
      <div
        className="bg-gradient-to-b from-[#5a3f26] to-[#2b1a0e] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-[#a0a0a021] shadow-[0_0_30px_rgba(255,255,255,0.05)] relative"
        aria-label={`Balance ${walletAmt}`}
      >
        <img
          src="/games/chess/Coin.png"
          alt=""
          className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10"
        />
        <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide">
          {fmt(walletAmt)}
        </span>
        <button
          type="button"
          onClick={onAddFunds}
          aria-label="Add funds"
          className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <img
            src="/games/chess/Plus.png"
            alt=""
            className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
          />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// BotActivateCountdown — displays "Match starts in Ns" under the queue
// progress text. The original copy ("Bot joining in Ns") leaked the
// presence of the bot to the player; renamed to neutral wording for
// platform-wide rule that the user must never know they're playing a
// bot. Same internal trigger (server's bot_activate_at timestamp) so
// the countdown is precise.
// ─────────────────────────────────────────────────────────────────────
function BotActivateCountdown({ ts }: { ts: number }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, Math.ceil((ts - Date.now()) / 1000)));
  useEffect(() => {
    const id = window.setInterval(() => {
      setRemaining(Math.max(0, Math.ceil((ts - Date.now()) / 1000)));
    }, 250);
    return () => window.clearInterval(id);
  }, [ts]);
  if (remaining <= 0) return null;
  return (
    <div className="chess-queue-botsoon">
      Match starts in {remaining}s
    </div>
  );
}

// Silence a lint warning — `playSlotLock` is imported for future use
// when we animate queue-slot fills with a per-seat lock sound. Not
// used today so referenced here to avoid "unused import" removal.
void playSlotLock;

// ─── Small helpers ──────────────────────────────────────────────────

// Parse a FEN's piece at an algebraic square. Tiny + local; avoids
// a second FEN parse from Board's useMemo pipeline.
function pieceAt(fen: string, sq: string): string | null {
  const layout = fen.split(' ')[0];
  if (!layout) return null;
  const file = sq.charCodeAt(0) - 97;
  const rank = sq.charCodeAt(1) - 49;
  if (file < 0 || file > 7 || rank < 0 || rank > 7) return null;
  const rows = layout.split('/');                 // FEN top-down rank 8 first
  const row = rows[7 - rank];
  let f = 0;
  for (const ch of row) {
    if (/[1-8]/.test(ch)) { f += Number(ch); continue; }
    if (f === file) {
      const colour = ch === ch.toUpperCase() ? 'w' : 'b';
      return colour + ch.toUpperCase();
    }
    f++;
  }
  return null;
}

// Find the king square for a side in a FEN string. Used to highlight
// the in-check square when the server flags `check:true`.
function findKingSquare(fen: string, side: 'w' | 'b'): string | null {
  const layout = fen.split(' ')[0];
  if (!layout) return null;
  const rows = layout.split('/');
  const target = side === 'w' ? 'K' : 'k';
  for (let r = 0; r < 8; r++) {
    const row = rows[7 - r];
    let f = 0;
    for (const ch of row) {
      if (/[1-8]/.test(ch)) { f += Number(ch); continue; }
      if (ch === target) return String.fromCharCode(97 + f) + String(r + 1);
      f++;
    }
  }
  return null;
}
